import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { formatErr } from "@/lib/api";
import { LockKeyhole, User, GitCommit, PlayCircle, BookOpen } from "lucide-react";

const formatBuildTime = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("tr-TR", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit"
    });
  } catch {
    return iso;
  }
};

// Sol paneldeki marka kartı — logolar için tutarlı çerçeve
const BrandCard = ({ src, name, tint, testId, objectFit = "cover" }) => (
  <div
    data-testid={testId}
    className="group relative overflow-hidden rounded-2xl bg-white/95 border border-white/40 aspect-square shadow-lg transition-transform duration-200 hover:-translate-y-1"
    title={name}
  >
    <img
      src={src}
      alt={name}
      className={`w-full h-full ${objectFit === "contain" ? "object-contain p-2" : "object-cover"} transition-transform duration-300 group-hover:scale-105`}
      loading="lazy"
    />
    <div
      className="absolute inset-x-0 bottom-0 h-1 opacity-90"
      style={{ backgroundColor: tint }}
      aria-hidden="true"
    />
  </div>
);

// YouTube/Vimeo URL → gömülebilir embed URL
const toEmbedUrl = (url) => {
  if (!url) return null;
  try {
    const u = new URL(url);
    if (u.hostname.includes("youtube.com")) {
      const v = u.searchParams.get("v");
      if (v) return `https://www.youtube.com/embed/${v}?autoplay=1&rel=0`;
    }
    if (u.hostname === "youtu.be") return `https://www.youtube.com/embed${u.pathname}?autoplay=1&rel=0`;
    if (u.hostname.includes("vimeo.com")) {
      const id = u.pathname.replace(/[^\d]/g, "");
      if (id) return `https://player.vimeo.com/video/${id}?autoplay=1`;
    }
  } catch { /* not a URL */ }
  return url; // ham iframe URL / mp4 URL
};

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [u, setU] = useState("");
  const [p, setP] = useState("");
  const [busy, setBusy] = useState(false);
  const [version, setVersion] = useState(null);
  const [videoUrl, setVideoUrl] = useState("");
  const [videoOpen, setVideoOpen] = useState(false);

  useEffect(() => {
    fetch(`${process.env.PUBLIC_URL || ""}/version.json`, { cache: "no-store" })
      .then((r) => (r.ok ? r.json() : null))
      .then((v) => v && setVersion(v))
      .catch(() => {});
    api.get("/public/config").then((r) => setVideoUrl(r.data?.tutorial_video_url || "")).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(u.trim(), p);
      toast.success("Hoş geldiniz");
      nav("/", { replace: true });
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Giriş başarısız");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-[#F9F8F6]">
      {/* Sol marka paneli — 3 marka için renkli vitrin */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        {/* Dinamik renk katmanları */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#E31E27] via-[#F27200] to-[#FFC700]" />
        <div className="absolute inset-0 opacity-40 mix-blend-multiply bg-[radial-gradient(circle_at_20%_20%,rgba(0,0,0,0.6),transparent_50%),radial-gradient(circle_at_80%_80%,rgba(0,0,0,0.6),transparent_50%)]" />
        {/* Grain texture için hafif noise */}
        <div
          className="absolute inset-0 opacity-[0.15] mix-blend-overlay pointer-events-none"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.6'/%3E%3C/svg%3E\")",
          }}
        />

        <div className="relative z-10 p-10 xl:p-14 flex flex-col justify-between text-white w-full">
          {/* Header */}
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-2xl bg-white/95 flex items-center justify-center font-bold font-display text-2xl text-[#E31E27] shadow-lg">
              K
            </div>
            <div>
              <div className="font-display text-xl font-bold tracking-tight">Hey Kasa Yönetim</div>
              <div className="text-[13px] text-white/85">Şube Bilançoları · Günlük Rapor</div>
            </div>
          </div>

          {/* Marka Kartları */}
          <div className="space-y-5">
            <div>
              <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur border border-white/25 rounded-full px-3 py-1 text-[11px] uppercase tracking-widest font-semibold mb-3">
                <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                Bir çatı, üç marka
              </div>
              <h1 className="font-display text-4xl xl:text-5xl font-bold tracking-tight leading-[1.05] text-white drop-shadow-sm">
                Her şubeniz,<br /><span className="italic">tek panelde.</span>
              </h1>
              <p className="text-white/90 text-base leading-relaxed mt-3 max-w-md">
                Günlük ciro, masraf, personel puantajı ve stok — Hey Burger, heydöner ve Wow&apos;s Fried Chicken şubeleri tek bir yerde.
              </p>
            </div>

            {/* 3 Marka Logo Kartı */}
            <div className="grid grid-cols-3 gap-3 max-w-lg" data-testid="brand-strip">
              <BrandCard
                src="https://customer-assets.emergentagent.com/job_daily-ledger-portal/artifacts/vid0cq47_HEYBURGER.jpg"
                name="Hey Burger"
                tint="#E31E27"
                testId="brand-heyburger"
              />
              <BrandCard
                src="https://customer-assets.emergentagent.com/job_daily-ledger-portal/artifacts/imcazygk_logo%20hey%20jpeg.PNG"
                name="heydöner"
                tint="#E31E27"
                testId="brand-heydoner"
                objectFit="contain"
              />
              <BrandCard
                src="https://customer-assets.emergentagent.com/job_daily-ledger-portal/artifacts/10wwjono_wfc%20logo.PNG"
                name="Wow's Fried Chicken"
                tint="#F27200"
                testId="brand-wowsfc"
                objectFit="contain"
              />
            </div>
          </div>

          <div className="flex items-center justify-between text-[11px] text-white/70 font-mono">
            <span>Hey Information &amp; Technology by Yasin</span>
            <span className="hidden xl:inline">© {new Date().getFullYear()}</span>
          </div>
        </div>
      </div>

      {/* Sağ form */}
      <div className="flex-1 flex items-center justify-center p-6 relative">
        {/* Sağ tarafın üst köşesinde soft renk vurgusu */}
        <div className="pointer-events-none absolute -top-24 -right-24 w-72 h-72 rounded-full bg-[#FFC700]/20 blur-3xl" aria-hidden="true" />
        <div className="pointer-events-none absolute -bottom-24 -left-16 w-72 h-72 rounded-full bg-[#E31E27]/10 blur-3xl" aria-hidden="true" />
        <div className="w-full max-w-md relative">
          <div className="lg:hidden flex items-center gap-2 mb-4">
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-[#E31E27] to-[#F27200] flex items-center justify-center text-white font-bold font-display shadow-md">K</div>
            <span className="font-display font-semibold text-lg">Hey Kasa Yönetim</span>
          </div>
          <div className="lg:hidden mb-6 grid grid-cols-3 gap-2" data-testid="brand-strip-mobile">
            <BrandCard
              src="https://customer-assets.emergentagent.com/job_daily-ledger-portal/artifacts/vid0cq47_HEYBURGER.jpg"
              name="Hey Burger"
              tint="#E31E27"
              testId="brand-heyburger-mobile"
            />
            <BrandCard
              src="https://customer-assets.emergentagent.com/job_daily-ledger-portal/artifacts/imcazygk_logo%20hey%20jpeg.PNG"
              name="heydöner"
              tint="#E31E27"
              testId="brand-heydoner-mobile"
              objectFit="contain"
            />
            <BrandCard
              src="https://customer-assets.emergentagent.com/job_daily-ledger-portal/artifacts/10wwjono_wfc%20logo.PNG"
              name="Wow's Fried Chicken"
              tint="#F27200"
              testId="brand-wowsfc-mobile"
              objectFit="contain"
            />
          </div>
          <h2 className="font-display text-3xl font-semibold mb-2">Giriş yap</h2>
          <p className="text-sm text-slate-500 mb-8">Şube veya yönetici hesabınızla oturum açın.</p>

          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="username" className="text-xs font-semibold uppercase tracking-wider text-slate-600">
                Kullanıcı adı
              </Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  id="username"
                  data-testid="login-username-input"
                  value={u}
                  onChange={(e) => setU(e.target.value)}
                  className="pl-9 h-11 bg-white"
                  placeholder="admin"
                  autoComplete="username"
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-wider text-slate-600">
                Şifre
              </Label>
              <div className="relative">
                <LockKeyhole className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  id="password"
                  data-testid="login-password-input"
                  type="password"
                  value={p}
                  onChange={(e) => setP(e.target.value)}
                  className="pl-9 h-11 bg-white"
                  placeholder="••••••••"
                  autoComplete="current-password"
                  required
                />
              </div>
            </div>
            <Button
              data-testid="login-submit-btn"
              type="submit"
              disabled={busy}
              className="w-full h-11 bg-gradient-to-r from-[#E31E27] via-[#F27200] to-[#FF8A00] hover:brightness-110 text-white font-semibold shadow-md shadow-[#E31E27]/20"
            >
              {busy ? "Giriş yapılıyor..." : "Giriş yap"}
            </Button>
          </form>

          <div className="mt-4 grid grid-cols-2 gap-2">
            {videoUrl && (
              <button
                type="button"
                onClick={() => setVideoOpen(true)}
                data-testid="tutorial-video-btn"
                className="inline-flex items-center justify-center gap-2 text-sm text-[#B8451F] bg-[#FFF6F1] border border-[#F5D3C0] rounded-lg py-2.5 hover:bg-[#FFEEDC] transition"
              >
                <PlayCircle className="h-4 w-4" />
                Eğitim Videosu
              </button>
            )}
            <Link
              to="/help"
              data-testid="tutorial-guide-link"
              className={`inline-flex items-center justify-center gap-2 text-sm text-slate-700 bg-[#F3F1EC] border border-[#EAE6DF] rounded-lg py-2.5 hover:bg-[#EAE6DF] transition ${!videoUrl ? "col-span-2" : ""}`}
            >
              <BookOpen className="h-4 w-4" />
              Kullanım Kılavuzu
            </Link>
          </div>
          {version && (
            <div
              data-testid="login-version-badge"
              className="mt-6 pt-4 border-t border-[#EAE6DF] flex items-center justify-center gap-3 text-[11px] text-slate-400"
            >
              <span className="inline-flex items-center gap-1 font-mono">
                <GitCommit className="h-3 w-3" />
                v{version.version}·{version.commit}
              </span>
              <span className="w-px h-3 bg-slate-300" aria-hidden="true" />
              <span>Son güncelleme: {formatBuildTime(version.buildTime)}</span>
            </div>
          )}
          {version?.changelog && version.changelog.length > 0 && (
            <div className="mt-3" data-testid="login-changelog">
              <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-2 text-center">Son Güncellemeler</div>
              <ul className="space-y-1.5">
                {version.changelog.slice(0, 3).map((c, i) => (
                  <li key={i} className="text-[11px] text-slate-500 flex gap-2 items-start" data-testid={`changelog-item-${i}`}>
                    <span className="font-mono text-[10px] text-slate-400 shrink-0 pt-0.5">{c.date}</span>
                    <span className="flex-1">
                      <span className="text-slate-700 font-medium">{c.title}</span>
                      {c.note && <span className="text-slate-500"> — {c.note}</span>}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* Tutorial video modal */}
      <Dialog open={videoOpen} onOpenChange={setVideoOpen}>
        <DialogContent className="max-w-3xl p-0 overflow-hidden">
          <DialogHeader className="px-5 pt-4 pb-2">
            <DialogTitle className="flex items-center gap-2 text-slate-900">
              <PlayCircle className="h-5 w-5 text-[#D95D39]" />
              Eğitim Videosu — Kasa Yönetim
            </DialogTitle>
          </DialogHeader>
          <div className="aspect-video w-full bg-black">
            {videoOpen && videoUrl && (
              /\.(mp4|webm|ogg)(\?|$)/i.test(videoUrl) ? (
                <video
                  data-testid="tutorial-video-frame"
                  src={videoUrl}
                  className="w-full h-full"
                  controls
                  autoPlay
                  playsInline
                />
              ) : (
                <iframe
                  data-testid="tutorial-video-frame"
                  src={toEmbedUrl(videoUrl)}
                  title="Eğitim Videosu"
                  className="w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              )
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
